import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { clientsAPI } from "../lib/api";
import { formatCurrency, formatDate, getStatusColor, CLIENT_STATUSES, CURRENCIES, TIMEZONES, convertTimeToTimezone } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Eye, Users } from "lucide-react";

export default function ClientsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    currency: "PHP",
    default_rate: "",
    billing_modes: ["hourly"],
    monthly_rate: "",
    shift_start: "",
    shift_end: "",
    client_timezone: "UTC",
    notes: "",
    status: "active",
  });

  useEffect(() => {
    fetchClients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchClients = async () => {
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const res = await clientsAPI.list(params);
      setClients(res.data);
    } catch (err) {
      toast.error("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.billing_modes.length === 0) {
      toast.error("Please select at least one billing mode");
      return;
    }
    const payload = {
      ...formData,
      default_rate: parseFloat(formData.default_rate) || 0,
      monthly_rate: parseFloat(formData.monthly_rate) || null,
    };
    try {
      if (editingClient) {
        await clientsAPI.update(editingClient.id, payload);
        toast.success("Client updated");
      } else {
        await clientsAPI.create(payload);
        toast.success("Client created");
      }
      setShowAddDialog(false);
      setEditingClient(null);
      resetForm();
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    try {
      await clientsAPI.delete(id);
      toast.success("Client deleted");
      fetchClients();
    } catch (err) {
      toast.error("Failed to delete client");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      currency: "PHP",
      default_rate: "",
      billing_modes: ["hourly"],
      monthly_rate: "",
      shift_start: "",
      shift_end: "",
      client_timezone: "UTC",
      notes: "",
      status: "active",
    });
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || "",
      email: client.email || "",
      phone: client.phone || "",
      address: client.address || "",
      currency: client.currency || "PHP",
      default_rate: client.default_rate != null ? String(client.default_rate) : "",
      billing_modes: client.billing_modes || ["hourly"],
      monthly_rate: client.monthly_rate != null ? String(client.monthly_rate) : "",
      shift_start: client.shift_start || "",
      shift_end: client.shift_end || "",
      client_timezone: client.client_timezone || "UTC",
      notes: client.notes || "",
      status: client.status || "active",
    });
    setShowAddDialog(true);
  };

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Clients</h1>
          <p className="text-muted-foreground mt-1">
            Manage your client relationships
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setEditingClient(null);
            setShowAddDialog(true);
          }}
          className="gap-2"
          data-testid="add-client-btn"
        >
          <Plus className="h-4 w-4" />
          Add Client
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-clients-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {CLIENT_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Clients Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : filteredClients.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No clients found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery ? "Try a different search term" : "Get started by adding your first client"}
            </p>
            {!searchQuery && (
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Client
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {filteredClients.map((client) => (
            <Card
              key={client.id}
              className="hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => navigate(`/clients/${client.id}`)}
              data-testid={`client-card-${client.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg truncate group-hover:text-brand-accent transition-colors">
                      {client.name}
                    </h3>
                    {client.email && (
                      <p className="text-sm text-muted-foreground truncate">
                        {client.email}
                      </p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/clients/${client.id}`);
                      }}>
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        openEditDialog(client);
                      }}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(client.id);
                        }}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <Badge className={getStatusColor(client.status)}>
                    {client.status}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {client.currency}
                  </span>
                  {(() => {
                    const modes = client.billing_modes || ["hourly"];
                    const parts = [];
                    if (modes.includes("hourly") && client.default_rate > 0)
                      parts.push(`${formatCurrency(client.default_rate, client.currency)}/hr`);
                    if (modes.includes("monthly") && client.monthly_rate > 0)
                      parts.push(`${formatCurrency(client.monthly_rate, client.currency)}/mo`);
                    if (modes.includes("commission"))
                      parts.push("Commission");
                    return parts.length > 0 ? (
                      <span className="text-sm font-mono">{parts.join(" · ")}</span>
                    ) : null;
                  })()}
                </div>
                {(client.shift_start || client.shift_end) && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-muted-foreground">
                      Shift ({client.client_timezone || "UTC"}): {client.shift_start || "?"} - {client.shift_end || "?"}
                    </p>
                    {user?.default_timezone && user.default_timezone !== client.client_timezone && (
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        Your Time ({user.default_timezone}): {convertTimeToTimezone(client.shift_start, client.client_timezone, user.default_timezone) || "?"} - {convertTimeToTimezone(client.shift_end, client.client_timezone, user.default_timezone) || "?"}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingClient ? "Edit Client" : "Add New Client"}
            </DialogTitle>
            <DialogDescription>
              {editingClient ? "Update client information" : "Create a new client to start tracking projects"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Client Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="client-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="client-phone-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                data-testid="client-address-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(val) => setFormData({ ...formData, currency: val })}
                >
                  <SelectTrigger data-testid="client-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code} - {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Billing Mode *</Label>
                <div className="flex gap-4 flex-wrap">
                  {["hourly", "monthly", "commission"].map((mode) => (
                    <label key={mode} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.billing_modes.includes(mode)}
                        onChange={(e) => {
                          const modes = e.target.checked
                            ? [...formData.billing_modes, mode]
                            : formData.billing_modes.filter((m) => m !== mode);
                          setFormData({ ...formData, billing_modes: modes });
                        }}
                        className="h-4 w-4"
                      />
                      <span className="text-sm capitalize">{mode}</span>
                    </label>
                  ))}
                </div>
              </div>
              {formData.billing_modes.includes("hourly") && (
                <div className="space-y-2">
                  <Label htmlFor="default_rate">Default Rate/hr</Label>
                  <Input
                    id="default_rate"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.default_rate}
                    onChange={(e) => setFormData({ ...formData, default_rate: e.target.value })}
                    data-testid="client-rate-input"
                  />
                </div>
              )}
              {formData.billing_modes.includes("monthly") && (
                <div className="space-y-2">
                  <Label htmlFor="monthly_rate">Monthly Rate</Label>
                  <Input
                    id="monthly_rate"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.monthly_rate}
                    onChange={(e) => setFormData({ ...formData, monthly_rate: e.target.value })}
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="shift_start">Shift Start</Label>
                <Input
                  id="shift_start"
                  type="time"
                  value={formData.shift_start}
                  onChange={(e) => setFormData({ ...formData, shift_start: e.target.value })}
                  data-testid="client-shift-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="shift_end">Shift End</Label>
                <Input
                  id="shift_end"
                  type="time"
                  value={formData.shift_end}
                  onChange={(e) => setFormData({ ...formData, shift_end: e.target.value })}
                  data-testid="client-shift-end-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="client_timezone">Client Timezone</Label>
              <Select
                value={formData.client_timezone}
                onValueChange={(val) => setFormData({ ...formData, client_timezone: val })}
              >
                <SelectTrigger data-testid="client-timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                rows="3"
                data-testid="client-notes-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(val) => setFormData({ ...formData, status: val })}
              >
                <SelectTrigger data-testid="client-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLIENT_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-client-btn">
                {editingClient ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
